import * as React from 'react';
import { useData } from '../../core/context/ProvidersContext';
import { IProductsConfig, IColorEntry, DEFAULT_PRODUCTS_CONFIG } from '../../config/productsConfig';
import { setProductsConfig } from '../shared/ProductColors';
import styles from './AdminPanel.module.scss';

type Section = 'products' | 'interventions';

export const ProductsManager: React.FC = () => {
  const data = useData();
  const [config, setConfig] = React.useState<IProductsConfig | null>(null);
  const [section, setSection] = React.useState<Section>('products');
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    data.getProductsConfig().then(cfg => setConfig(cfg));
  }, [data]);

  if (!config) return <div className={styles.loading}>Chargement…</div>;

  const entries = config[section];

  const updateEntry = (key: string, patch: Partial<IColorEntry>): void => {
    const next = {
      ...config,
      [section]: { ...entries, [key]: { ...entries[key], ...patch } },
    };
    setConfig(next);
    setDirty(true);
  };

  const removeEntry = (key: string): void => {
    const copy = { ...entries };
    delete copy[key];
    setConfig({ ...config, [section]: copy });
    setDirty(true);
  };

  const addEntry = (): void => {
    const key = prompt('Nom du nouveau ' + (section === 'products' ? 'produit' : 'statut') + ' :');
    if (!key) return;
    if (entries[key]) { alert('Existe déjà'); return; }
    setConfig({
      ...config,
      [section]: { ...entries, [key]: { background: '#cccccc', text: '#000000', label: key } },
    });
    setDirty(true);
  };

  const handleSave = async (): Promise<void> => {
    if (!data.saveProductsConfig) {
      alert('La sauvegarde n\'est pas supportée dans ce mode (SharePoint).');
      return;
    }
    setSaving(true);
    try {
      await data.saveProductsConfig(config);
      setProductsConfig(config);
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (): void => {
    if (!confirm('Restaurer les couleurs par défaut ?')) return;
    setConfig(DEFAULT_PRODUCTS_CONFIG);
    setDirty(true);
  };

  return (
    <div className={styles.crud}>
      <h3>Produits &amp; couleurs</h3>
      <div className={styles.subTabs}>
        <button
          className={section === 'products' ? styles.subTabActive : styles.subTab}
          onClick={() => setSection('products')}
        >Produits</button>
        <button
          className={section === 'interventions' ? styles.subTabActive : styles.subTab}
          onClick={() => setSection('interventions')}
        >Interventions</button>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Code</th><th>Libellé</th><th>Fond</th><th>Texte</th><th>Aperçu</th><th></th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(entries).map(key => {
            const e = entries[key];
            return (
              <tr key={key}>
                <td><code>{key}</code></td>
                <td>
                  <input
                    className={styles.input}
                    value={e.label}
                    onChange={ev => updateEntry(key, { label: ev.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="color"
                    value={e.background}
                    onChange={ev => updateEntry(key, { background: ev.target.value })}
                  />
                </td>
                <td>
                  <input
                    type="color"
                    value={e.text}
                    onChange={ev => updateEntry(key, { text: ev.target.value })}
                  />
                </td>
                <td>
                  <span
                    className={styles.preview}
                    style={{ background: e.background, color: e.text }}
                  >{e.label || key}</span>
                </td>
                <td>
                  <button className={styles.btnSmallDanger} onClick={() => removeEntry(key)}>×</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className={styles.addRow}>
        <button className={styles.btnSmall} onClick={addEntry}>+ Ajouter</button>
        <button className={styles.btnSmall} onClick={handleReset}>Réinitialiser</button>
        <button
          className={styles.btnPrimary}
          onClick={handleSave}
          disabled={!dirty || saving}
        >{saving ? 'Sauvegarde…' : 'Enregistrer'}</button>
      </div>
    </div>
  );
};
